'use client';

import { c as _c } from "react/compiler-runtime";
import { usePathname, useRouter } from 'next/navigation';
import { useLocale, useTranslations } from 'next-intl';
import { useEffect } from 'react';
import { CheckoutProgressStepper } from '~/components/CheckoutProgressStepper';
import { getPathname } from '~/i18n/navigation';
import { TRIP_SECTION_LABEL_KEYS, TRIP_SECTIONS, type TripSection, useTripJourneyActions } from '~/stores/tpv2/journeyStore';
import styles from './TripProgressStepper.module.css';
const CHECKOUT_SECTION_PATHS: Partial<Record<TripSection, string>> = {
  payment: '/checkout/details',
  'passenger-details': '/checkout/passengers'
};
const getStepIndexFromPathname = (pathname: string): number => {
  const checkoutIndex = TRIP_SECTIONS.findIndex(section => {
    const checkoutPath = CHECKOUT_SECTION_PATHS[section];
    return checkoutPath !== undefined && pathname.includes(checkoutPath);
  });
  if (checkoutIndex >= 0) return checkoutIndex;
  const segments = pathname.split('/').filter(Boolean);
  for (const segment of segments) {
    const index = TRIP_SECTIONS.indexOf(segment as (typeof TRIP_SECTIONS)[number]);
    if (index >= 0) return index;
  }
  return 0;
};
const getPathnameForStepIndex = (index: number, basePath: string, tripId: string, locale: string): string => {
  const checkoutPath = CHECKOUT_SECTION_PATHS[TRIP_SECTIONS[index]];
  if (checkoutPath) return getPathname({
    href: `${checkoutPath}?tripId=${tripId}`,
    locale
  });
  const base = basePath.replace(/\/$/, '') || '/';
  if (index <= 0) return `${base}/${tripId}`;
  return `${base}/${TRIP_SECTIONS[index]}/${tripId}`;
};
const StepperFrame = () => {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <CheckoutProgressStepper.Frame><CheckoutProgressStepper.TopRow><CheckoutProgressStepper.BackButton /><CheckoutProgressStepper.Steps /></CheckoutProgressStepper.TopRow><CheckoutProgressStepper.ProgressBar /></CheckoutProgressStepper.Frame>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
};
export interface TripProgressStepperProps {
  basePath?: string;
  tripId: string;
}
export const TripProgressStepper = t0 => {
  const $ = _c(23);
  const {
    basePath: t1,
    tripId
  } = t0;
  const basePath = t1 === undefined ? "/poc-v2" : t1;
  const router = useRouter();
  const pathname = usePathname();
  const locale = useLocale();
  const t = useTranslations();
  const currentStepIndex = getStepIndexFromPathname(pathname ?? "");
  const {
    setActiveSection,
    setCurrentStepIndex
  } = useTripJourneyActions();
  let t2;
  if ($[0] !== t) {
    t2 = TRIP_SECTIONS.map(section => t(TRIP_SECTION_LABEL_KEYS[section]));
    $[0] = t;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const steps = t2;
  let t3;
  let t4;
  if ($[2] !== currentStepIndex || $[3] !== setActiveSection || $[4] !== setCurrentStepIndex) {
    t3 = () => {
      setCurrentStepIndex(currentStepIndex);
      setActiveSection(TRIP_SECTIONS[currentStepIndex]);
    };
    t4 = [currentStepIndex, setActiveSection, setCurrentStepIndex];
    $[2] = currentStepIndex;
    $[3] = setActiveSection;
    $[4] = setCurrentStepIndex;
    $[5] = t3;
    $[6] = t4;
  } else {
    t3 = $[5];
    t4 = $[6];
  }
  useEffect(t3, t4);
  let t5;
  if ($[7] !== basePath || $[8] !== currentStepIndex || $[9] !== locale || $[10] !== router || $[11] !== tripId) {
    t5 = () => {
      if (currentStepIndex > 0) {
        router.push(getPathnameForStepIndex(currentStepIndex - 1, basePath, tripId, locale));
      } else {
        router.back();
      }
    };
    $[7] = basePath;
    $[8] = currentStepIndex;
    $[9] = locale;
    $[10] = router;
    $[11] = tripId;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  const onBack = t5;
  const t6 = currentStepIndex > 0 ? onBack : undefined;
  let t7;
  if ($[13] !== basePath || $[14] !== locale || $[15] !== tripId) {
    t7 = index => getPathnameForStepIndex(index, basePath, tripId, locale);
    $[13] = basePath;
    $[14] = locale;
    $[15] = tripId;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  let t8;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <StepperFrame />;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  let t9;
  if ($[18] !== currentStepIndex || $[19] !== steps || $[20] !== t6 || $[21] !== t7) {
    t9 = <div className={styles.wrapper}><CheckoutProgressStepper.Provider steps={steps} currentStepIndex={currentStepIndex} onBack={t6} hrefForStep={t7}>{t8}</CheckoutProgressStepper.Provider></div>;
    $[18] = currentStepIndex;
    $[19] = steps;
    $[20] = t6;
    $[21] = t7;
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  return t9;
};
export const TripProgressStepperPlaceholder = () => {
  const $ = _c(5);
  const t = useTranslations();
  let t0;
  if ($[0] !== t) {
    t0 = TRIP_SECTIONS.map(section => t(TRIP_SECTION_LABEL_KEYS[section]));
    $[0] = t;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const steps = t0;
  let t1;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <StepperFrame />;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== steps) {
    t2 = <div className={styles.wrapper}><CheckoutProgressStepper.Provider steps={steps} currentStepIndex={0}>{t1}</CheckoutProgressStepper.Provider></div>;
    $[3] = steps;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return t2;
};